import ToolTip from "@components/Tooltip";
import {
  makeSelectOne,
  makeSelectIsAvailableVariant,
  makeSelectOfficeOptions,
  makeSelectOfficeOptionsNorm,
} from "@features/publicPage/schemeDetails/selectors";
import { useDispatch, useSelector } from "react-redux";
import PublicPriceComponent from "./PriceComponent";
import Button from "@components/Button";
import {
  useLoadPublicOfficeOptionsQuery,
  usePlaceOrderPublicSchemeMutation,
} from "@features/publicPage/schemeDetails/api";
import { enqueueSnackbar } from "@features/auth/authSlice";
import { RiShoppingBag2Line } from "react-icons/ri";
import { useState } from "react";
import Dialog from "@components/Dialog";
import InputWrapper from "@components/Input";
import TextAreaWrapper from "@components/Textarea";
import AddressSelector from "@components/AddressSelector";
import { orderSchema } from "../validation/customerInfoSch";
import { validator } from "@utils/helpers";
import SelectWrapper from "@components/Select";

const PublicSchemePriceDetails = ({ scheme }) => {
  const dispatch = useDispatch();

  const [openCustomerInfo, setOpenCustomerInfo] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({});
  const [errors, setErrors] = useState({});

  const one = useSelector(makeSelectOne);
  const variant = one?.variant_id || {};
  const isAvailbleToBuy = useSelector(makeSelectIsAvailableVariant);
  const officeOptions = useSelector(makeSelectOfficeOptions);
  const officeOptionsNorm = useSelector(makeSelectOfficeOptionsNorm);

  // place order;
  const [placeOrder, { isLoading: placeLoading }] =
    usePlaceOrderPublicSchemeMutation();
  useLoadPublicOfficeOptionsQuery(customerInfo?.vdc_municipality?._id, {
    skip: !customerInfo?.vdc_municipality?._id,
    refetchOnMountOrArgChange: true,
  });

  const handleOpenCustomerDesc = () => {
    if (isAvailbleToBuy) {
      setOpenCustomerInfo(true);
    } else {
      dispatch(
        enqueueSnackbar({
          message: "Please another variant. This variant is not availble.",
          options: { variant: "warning" },
        })
      );
    }
  };

  const handleCloseCustomerDesc = () => {
    setOpenCustomerInfo(false);
    setCustomerInfo({});
  };

  const handleChange = (key) => (e) => {
    setOneValue({ key, value: e.target.value });
  };

  const handleDropdown = (key) => (e) => {
    setOneValue({ key, value: e.value });
  };

  const handleSelectLocations = ({ _id, name, key }) => {
    setOneValue({
      key,
      value: {
        _id,
        name,
      },
    });
  };

  const setOneValue = ({ key, value }) => {
    setCustomerInfo((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        let newErr = { ...prev };
        delete newErr[key];
        return newErr;
      });
    }
  };

  const handlePlaceOrder = () => {
    const errorObj = validator(orderSchema, {
      customer_name: customerInfo?.customer_name,
      customer_phone: customerInfo?.customer_phone,
      customer_address: customerInfo?.customer_address,
      state: customerInfo?.state?._id,
      district: customerInfo?.district?._id,
      office_id: customerInfo?.office_id,
      vdc_municipality: customerInfo?.vdc_municipality?._id,
    });

    if (errorObj) {
      setErrors(errorObj);
    } else {
      const data = {
        ...customerInfo,
        state: customerInfo?.state?._id,
        district: customerInfo?.district?._id,
        vdc_municipality: customerInfo?.vdc_municipality?._id,
        scheme_id: scheme?._id,
        scheme_type: scheme?.type,
        variant_id: one?.variant_id?._id,
      };
      setErrors({});
      placeOrder(data)
        .unwrap()
        .then((res) => {
          if (res?.success) {
            dispatch(
              enqueueSnackbar({
                message: res?.msg || "Order Placed Successfully.",
                options: { variant: "success" },
              })
            );
            handleCloseCustomerDesc();
          }
        })
        .catch((err) => {
          dispatch(
            enqueueSnackbar({
              message: err?.data?.msg || "Order Placed Failure.",
              options: { variant: "warning" },
            })
          );
        });
    }
  };

  return (
    <>
      <div className='text-sm p-3 border border-gray-300 rounded lg:border-none'>
        <GridContainer>
          <div className='text-right flex justify-end items-center'>
            Scheme Price
          </div>
          <div>
            <PublicPriceComponent
              price={scheme?.actual_price}
              sales_price={scheme?.offer_price}
              className='mt-0'
              isReversed
            />
          </div>
        </GridContainer>
        <GridContainer className='border-b border-gray-300 pb-2 mb-2'>
          <div className='text-right flex items-center gap-1 justify-end'>
            {scheme?.main_product?.is_multiple_variation && (
              <ToolTip
                toolTipContent=' Price of the product may vary with different variant of the
              product.'
              />
            )}
            <div
              className={
                !scheme?.main_product?.is_multiple_variation ? "mb-1" : ""
              }
            >
              Product Price
            </div>
          </div>
          <div>
            <PublicPriceComponent
              price={variant?.price}
              sales_price={variant?.sales_price}
              className='mt-0'
              isReversed
            />
          </div>
        </GridContainer>
        <GridContainer>
          <div className='text-right flex items-center gap-1 justify-end'>
            Total Price
          </div>
          <div>
            <PublicPriceComponent
              price={variant?.sales_price + scheme?.offer_price}
              sales_price={variant?.sales_price + scheme?.offer_price}
              className='mt-0'
            />
          </div>
        </GridContainer>
        <Button
          leftSection={<RiShoppingBag2Line />}
          onClick={handleOpenCustomerDesc}
          fullWidth
          className='mt-2'
        >
          Place Order
        </Button>
      </div>
      <Dialog
        opened={openCustomerInfo}
        title='Your Detailed Information'
        onClose={handleCloseCustomerDesc}
        size='70%'
      >
        <div className='mb-2 font-semibold text-sm'>
          Please fill up your information before placing the order.
        </div>
        <div className='grid grid-cols-2 gap-4 mb-4'>
          <InputWrapper
            label='Full Name'
            value={customerInfo?.customer_name || ""}
            onChange={handleChange("customer_name")}
            id='customer_name'
            divclassname='w-full'
            withAsterisk
            error={errors?.customer_name || null}
          />
          <InputWrapper
            label='Phone No.'
            value={customerInfo?.customer_phone || ""}
            onChange={handleChange("customer_phone")}
            id='customer_phone'
            divclassname='w-full'
            withAsterisk
            type='number'
            error={errors?.customer_phone || null}
          />
          <InputWrapper
            label='Email Address (Optional)'
            value={customerInfo?.customer_email || ""}
            onChange={handleChange("customer_email")}
            id='customer_email'
            divclassname='w-full'
          />
          <InputWrapper
            label='Address'
            value={customerInfo?.customer_address || ""}
            onChange={handleChange("customer_address")}
            id='customer_address'
            divclassname='w-full'
            withAsterisk
            error={errors?.customer_address || null}
          />
        </div>
        <AddressSelector
          addressValue={{
            state: customerInfo?.state,
            district: customerInfo?.district,
            vdc_municipality: customerInfo?.vdc_municipality,
          }}
          handleSelectLocations={handleSelectLocations}
          fullWidth
          selectorWidthClass='w-1/2'
          errors={errors}
        />
        {customerInfo?.vdc_municipality?._id && (
          <SelectWrapper
            options={officeOptions}
            label='Select Your Office'
            onChange={handleDropdown("office_id")}
            value={officeOptionsNorm[customerInfo?.office_id] || ""}
            error={errors?.office_id}
          />
        )}
        <TextAreaWrapper
          label='Current Device (Optional)'
          value={customerInfo?.old_product_information || ""}
          onChange={handleChange("old_product_information")}
          id='old_product_information'
          className='mb-4'
        />
        <Button
          leftSection={<RiShoppingBag2Line />}
          onClick={handlePlaceOrder}
          loading={placeLoading}
        >
          Place Your Order
        </Button>
      </Dialog>
    </>
  );
};

export default PublicSchemePriceDetails;

const GridContainer = ({ children, className }) => {
  return (
    <div className={`grid grid-cols-2 gap-2 ${className || ""}`}>
      {children}
    </div>
  );
};
